import React from 'react'
import toArray from 'lodash/toArray'

interface DndDroppableProps {
    tag?: keyof JSX.IntrinsicElements
    wrapperComponent?: React.ReactElement | any
    dropEnabled?: boolean
    types?: string[]
    className?: string
    children?: React.ReactNode
    data?: any
    onDrop?: (data: { dnd_item: string }, e: any) => void
    onDragOver?: (event: React.DragEvent) => void
    onDragEnter?: (event: React.DragEvent) => void
    onDragLeave?: (event: React.DragEvent) => void
    onMouseOver?: (event: React.MouseEvent) => void
    dragEnabled?: boolean
    onDragStart?: (event: any) => void
    onDragEnd?: (event: any) => void
    onClick?: (e: React.MouseEvent) => void
    id?: string
    onMouseDown?: (e: React.MouseEvent) => void
    onMouseEnter?: (e: React.MouseEvent) => void
    onMouseLeave?: (e: React.MouseEvent) => void
}

interface DndDroppableState {
    over: boolean
}

interface Position {
    top: number
    left: number
    right: number
    bottom: number
}

class DndDroppable extends React.Component<DndDroppableProps, DndDroppableState> {
    static defaultProps: Partial<DndDroppableProps> = {
        tag: 'div',
        types: ['dnd_item'],
        dropEnabled: true,
    }

    state: DndDroppableState = {
        over: false,
    }

    position!: Position
    droppableRef = React.createRef<HTMLDivElement>()

    render() {
        let Tag: any = this.props.tag
        let props = { ...this.props }

        if (this.props.wrapperComponent) {
            Tag = this.props.wrapperComponent.type as keyof JSX.IntrinsicElements
            props = { ...props, ...this.props.wrapperComponent.props }
        }

        let classes = 'Droppable'
        if (props.className) classes += ` ${props.className}`
        if (this.state.over) classes += ' over'

        return (
            <Tag
                ref={this.droppableRef}
                className={classes}
                {...filterProps(props)}
                onDrop={this.onDrop.bind(this)}
                onDragOver={this.onDragOver.bind(this)}
                onDragEnter={this.onDragEnter.bind(this)}
                onDragLeave={this.onDragLeave.bind(this)}
                onDragExit={this.onDragLeave.bind(this)}
            >
                {props.children}
            </Tag>
        )
    }

    onDragOver(e: React.DragEvent) {
        e.preventDefault()
        if (!this.allowed(pickTypes(e))) {
            return
        }
        if (typeof this.props.onDragOver === 'function') {
            this.props.onDragOver(e)
        }
    }

    onDragEnter(e: React.DragEvent) {
        e.preventDefault()
        if (this.state.over) {
            return
        }
        if (!this.allowed(pickTypes(e))) {
            return
        }
        if (typeof this.props.onDragEnter === 'function') {
            this.props.onDragEnter(e)
        }
        this.setState({ over: true })
    }

    onDragLeave(e: React.DragEvent) {
        e.preventDefault()
        if (!this.allowed(pickTypes(e))) {
            return
        }

        let over = true
        const rect = this.position
        if (e.clientX <= rect.left || e.clientX >= rect.right) {
            over = false
        }
        if (e.clientY <= rect.top || e.clientY >= rect.bottom) {
            over = false
        }
        if (over) {
            return
        }

        this.setState({ over: false })
        if (typeof this.props.onDragLeave === 'function') {
            this.props.onDragLeave(e)
        }
    }

    onDrop(e: React.DragEvent) {
        e.preventDefault()
        if (!this.allowed(pickTypes(e))) {
            return
        }
        this.setState({ over: false })

        let props = { ...this.props }
        if (this.props.wrapperComponent) {
            props = { ...props, ...this.props.wrapperComponent.props }
        }

        const data: any = !props.types
            ? null
            : ([] as string[]).concat(props.types).reduce((d: { [key: string]: string }, type: string) => {
                  d[type] = e.dataTransfer.getData(type)
                  return d
              }, {})

        if (typeof this.props.onDrop === 'function') {
            this.props.onDrop(data, e)
        }
    }

    allowed(attemptingTypes: string[]) {
        let props = { ...this.props }
        if (this.props.wrapperComponent) {
            props = { ...props, ...this.props.wrapperComponent.props }
        }
        if (!props.dropEnabled) {
            return false
        }

        const _attemptingTypes = toArray(attemptingTypes)
        if (!props.types) {
            return true
        }
        return ([] as string[]).concat(props.types).reduce((sum, type) => {
            if (_attemptingTypes.indexOf(type) >= 0) {
                return true
            }

            return sum
        }, false)
    }

    componentDidMount() {
        const node = this.droppableRef.current
        if (node) {
            this.position = {
                top: node.offsetTop + 5,
                left: node.offsetLeft + 5,
                right: node.offsetLeft + node.offsetWidth - 5,
                bottom: node.offsetTop + node.offsetHeight - 5,
            }
        }
    }
}

function pickTypes(e: React.DragEvent): any {
    return e.dataTransfer ? e.dataTransfer.types : []
}

function filterProps(props: DndDroppableProps): Partial<DndDroppableProps> {
    const forbidden = ['types', 'className', 'dropEnabled', 'wrapperComponent']
    return Object.keys(props).reduce((filteredProps: Partial<DndDroppableProps>, key) => {
        if (!forbidden.includes(key)) {
            ;(filteredProps as any)[key] = (props as any)[key]
        }
        return filteredProps
    }, {})
}

export default DndDroppable
